(() => {
  const MIN_QUERY_LENGTH = 18;
  const MIN_QUERY_WORDS = 3;
  const root = window.Shopify?.routes?.root || '/';
  const cache = new Map();
  const timers = new WeakMap();

  const normalize = (value) =>
    value
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^\p{L}\p{N}]+/gu, ' ')
      .trim()
      .replace(/\s+/g, ' ');

  const shouldCheckExactTitle = (query) => {
    const normalized = normalize(query);

    return (
      query.length >= MIN_QUERY_LENGTH &&
      normalized.split(' ').filter(Boolean).length >= MIN_QUERY_WORDS
    );
  };

  const getSearchUrl = () => {
    const rootWithSlash = root.endsWith('/') ? root : `${root}/`;

    return new URL(
      `${rootWithSlash}search`,
      window.location.origin
    );
  };

  async function findExactTitle(query) {
    const key = normalize(query);

    if (cache.has(key)) {
      return cache.get(key);
    }

    const escapedPhrase = query
      .replace(/\\/g, '\\\\')
      .replace(/"/g, '\\"');

    const url = getSearchUrl();

    url.searchParams.set('q', `"${escapedPhrase}"`);
    url.searchParams.set('type', 'product');
    url.searchParams.set(
      'section_id',
      'exact-title-search'
    );

    const response = await fetch(url.toString(), {
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    if (!response.ok) {
      throw new Error(
        `Exact title search failed: ${response.status}`
      );
    }

    const html = await response.text();

    const parsedDocument = new DOMParser().parseFromString(
      html,
      'text/html'
    );

    const candidates = Array.from(
      parsedDocument.querySelectorAll(
        'template[data-exact-title-result]'
      )
    );

    const exact = candidates.find(
      (candidate) =>
        normalize(candidate.dataset.title || '') === key
    );

    const match = exact
      ? {
          key,
          title: exact.dataset.title || query,
          url: new URL(
            exact.dataset.url || '/',
            window.location.origin
          ).toString(),
          cardHtml: exact.innerHTML.trim(),
        }
      : null;

    cache.set(key, match);

    return match;
  }

  function removeInjectedCards(input) {
    input
      .closest('predictive-search-component')
      ?.querySelectorAll(
        '[data-exact-title-injected]'
      )
      .forEach((element) => element.remove());
  }

  function insertExactCard(input, match) {
    const component = input.closest(
      'predictive-search-component'
    );

    if (!component || !match.cardHtml) {
      return;
    }

    const resultsContainer =
      component.querySelector(
        '[ref="predictiveSearchResults"]'
      ) || component;

    let inserting = false;

    const insert = () => {
      if (
        inserting ||
        normalize(input.value) !== match.key
      ) {
        return;
      }

      const list = component.querySelector(
        '.predictive-search-results__wrapper-products'
      );

      if (!list) {
        return;
      }

      const existingInjected = list.querySelector(
        '[data-exact-title-injected]'
      );

      if (
        existingInjected?.dataset.exactUrl === match.url
      ) {
        return;
      }

      inserting = true;

      existingInjected?.remove();

      const targetPath = new URL(
        match.url,
        window.location.origin
      ).pathname;

      list.querySelectorAll('li').forEach((item) => {
        const link = item.querySelector('a[href]');

        if (
          link &&
          new URL(
            link.href,
            window.location.origin
          ).pathname === targetPath
        ) {
          item.remove();
        }
      });

      const template =
        document.createElement('template');

      template.innerHTML = match.cardHtml;

      const card =
        template.content.firstElementChild;

      if (card) {
        card.setAttribute(
          'data-exact-title-injected',
          'true'
        );

        card.setAttribute(
          'data-exact-url',
          match.url
        );

        list.prepend(card);
      }

      inserting = false;
    };

    const observer = new MutationObserver(insert);

    observer.observe(resultsContainer, {
      childList: true,
      subtree: true,
    });

    insert();

    window.setTimeout(() => {
      insert();
      observer.disconnect();
    }, 1500);
  }

  async function openExactOrSearch(input) {
    const query = input.value.trim();

    if (!query) {
      return;
    }

    input.setAttribute('aria-busy', 'true');

    try {
      const match = await findExactTitle(query);

      if (match) {
        window.location.assign(match.url);
        return;
      }
    } catch (error) {
      console.warn(
        '[Exact title search]',
        error
      );
    } finally {
      input.removeAttribute('aria-busy');
    }

    const url = new URL(
      input.form?.action || getSearchUrl(),
      window.location.origin
    );

    url.searchParams.set('q', query);
    url.searchParams.set('type', 'product');
    url.searchParams.set(
      'options[prefix]',
      'last'
    );

    window.location.assign(url.toString());
  }

  document.addEventListener(
    'input',
    (event) => {
      const input = event.target;

      if (
        !(input instanceof HTMLInputElement) ||
        input.type !== 'search' ||
        input.name !== 'q'
      ) {
        return;
      }

      const previousTimer = timers.get(input);

      if (previousTimer) {
        window.clearTimeout(previousTimer);
      }

      removeInjectedCards(input);

      const query = input.value.trim();

      if (!shouldCheckExactTitle(query)) {
        return;
      }

      const timer = window.setTimeout(
        async () => {
          try {
            const match =
              await findExactTitle(query);

            if (
              match &&
              normalize(input.value) === match.key
            ) {
              insertExactCard(input, match);
            }
          } catch (error) {
            console.warn(
              '[Exact title search]',
              error
            );
          }
        },
        450
      );

      timers.set(input, timer);
    },
    true
  );

  document.addEventListener(
    'keydown',
    (event) => {
      const input = event.target;

      if (
        !(input instanceof HTMLInputElement) ||
        input.type !== 'search' ||
        input.name !== 'q' ||
        event.key !== 'Enter' ||
        event.isComposing
      ) {
        return;
      }

      const query = input.value.trim();

      if (!shouldCheckExactTitle(query)) {
        return;
      }

      const selectedResult = input
        .closest('predictive-search-component')
        ?.querySelector(
          '[aria-selected="true"]'
        );

      if (selectedResult) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      void openExactOrSearch(input);
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;

      if (!(form instanceof HTMLFormElement)) {
        return;
      }

      const input = form.querySelector(
        'input[type="search"][name="q"]'
      );

      if (
        !(input instanceof HTMLInputElement) ||
        !form.action
      ) {
        return;
      }

      const action = new URL(
        form.action,
        window.location.origin
      );

      if (
        !action.pathname.endsWith('/search') ||
        !shouldCheckExactTitle(
          input.value.trim()
        )
      ) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      void openExactOrSearch(input);
    },
    true
  );
})();